// ============================================================
// LOVERIFT - Client Entry Point
// ============================================================
//
// Routes (same origin as the API; works behind IIS + PM2):
//   `/`       — public marketing / home
//   `/login`  — Better Auth sign-in & sign-up → Godot at `/game/`
//   `/game/`  — Godot web client (auth gate runs in lr-avatar-setup.js)
//
// Legacy `/play` redirects to `/game/`.

/// <reference types="vite/client" />

import {
  ensureAuthenticatedUser,
  getAuthUserOrNull,
  initialAuthTabFromUrl,
  mountAuthScreen,
} from './auth/authGate';
import { hideLanding, showLanding } from './site/landing';

/** Canonical post-login destination (Godot export). */
export const GAME_PATH = '/game';

function clearAuthRedirectGuard(): void {
  try {
    sessionStorage.removeItem('lr-auth-redirect-ts');
  } catch {
    /* ignore */
  }
}

/** Normalizes `return` query targets; maps legacy `/play` → `/game`. */
export function normalizeReturnPath(raw: string | null): string {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return GAME_PATH;
  const path = raw.replace(/\/+$/, '') || '/';
  if (path === '/play' || path === '/game') return GAME_PATH;
  return raw;
}

/** Browser URL for the Godot shell (trailing slash keeps asset paths stable). */
export function gameEntryUrl(returnPath?: string): string {
  const path = normalizeReturnPath(returnPath ?? GAME_PATH);
  if (path === GAME_PATH) return '/game/';
  return path.endsWith('/') ? path : `${path}/`;
}

async function handleLoginRoute(): Promise<void> {
  const qs = window.location.search || '';
  if (!window.location.pathname.endsWith('.html')) {
    window.location.replace('/login.html' + qs);
    return;
  }

  document.body.classList.add('site-mode-login');
  document.body.classList.remove('site-mode-landing');
  hideLanding();

  const params = new URLSearchParams(window.location.search);
  const dest = gameEntryUrl(normalizeReturnPath(params.get('return')));

  mountAuthScreen(initialAuthTabFromUrl());

  const existing = await Promise.race([
    getAuthUserOrNull(),
    new Promise<null>((resolve) => setTimeout(() => resolve(null), 4000)),
  ]);
  if (existing) {
    clearAuthRedirectGuard();
    window.location.replace(dest);
    return;
  }

  await ensureAuthenticatedUser(initialAuthTabFromUrl());
  clearAuthRedirectGuard();
  window.location.replace(dest);
}

async function handleHomeRoute(): Promise<void> {
  document.body.classList.remove('site-mode-login');
  await showLanding();
}

async function route(): Promise<void> {
  const rawPath = window.location.pathname;
  const path = rawPath.replace(/\/+$/, '') || '/';
  const tail = window.location.search + window.location.hash;

  if (path === '/login') {
    await handleLoginRoute();
    return;
  }
  // Already at the canonical Godot path. This SPA fallback only ever sees `/game/`
  // when the real static loader (client/dist/game/index.html) is missing — a build
  // that hasn't run, or a broken deploy. Redirecting to `/game/` here would replace
  // the URL with itself (trailing-slash stripping above collapses `/game/` to `/game`,
  // which the next branch treats the same as the legacy `/play`), reloading forever.
  if (rawPath === '/game/') {
    const loading = document.getElementById('loading');
    const status = document.getElementById('load-status');
    if (loading && status) {
      loading.style.display = 'flex';
      status.textContent = 'Game client not built. Run `npm run build` (needs client/dist/game/index.html).';
    }
    return;
  }
  if (path === '/play' || path === '/game') {
    window.location.replace(`/game/${tail}`);
    return;
  }
  // SPA must not handle Godot paths (missing play.html used to fall through to index → redirect / → loop).
  if (path.startsWith('/game/') || path === '/game/play' || path === '/game/play.html') {
    window.location.replace('/game/' + tail);
    return;
  }
  if (path === '/' || path === '') {
    await handleHomeRoute();
    return;
  }
  if (path === '/friends' || path === '/friends.html') {
    window.location.replace('/friends.html');
    return;
  }

  window.location.replace('/');
}

function markAppBooted(): void {
  document.documentElement.classList.remove('lr-boot-landing', 'lr-boot-login');
  const w = window as Window & { __lrMarkAppBooted?: () => void };
  w.__lrMarkAppBooted?.();
}

async function boot(): Promise<void> {
  try {
    await route();
  } catch (err) {
    console.error(err);
    const loading = document.getElementById('loading');
    const status = document.getElementById('load-status');
    if (loading && status) {
      loading.style.display = 'flex';
      status.textContent = 'Could not start — try a hard refresh or sign in from /login';
    }
  } finally {
    markAppBooted();
  }
}

boot();
